/**
 * 공식 계통도의 **층 → 계통** 선언.
 *
 * KAERI 공식 계통도(`docs/SMR_도식.pptx`)의 도형 이름은 층을 적는다(`P__CVCS__212` 의 `P__CVCS`). 이 표가
 * 그 층을 카탈로그 계통(`types/scope.ts`)에 잇는다 — 그림 요소와 선택기의 계통이 1:1 로 짝이 맞는 근거다.
 * 한 층이 여러 계통을 담는 곳(원자로 모듈 `P__Reactor`: 용기·가압기·노심·SG·RCS)은 도형 이름 단위로 가른다.
 * 틀(범례·배경)은 `frame` — 어느 계통도 아니고 켜지지 않는다.
 *
 * 공식 목록에 없는 요소는 그것이 속한 계통에 둔다: DHR 풀 → PDHR, 억압 풀 → 격납용기, Heat sink·SW 펌프
 * → 순환수·기기냉각수(그림 9 에서 BOP 로 켜짐), Grid·Station loads → ELS, 바이패스 → MSS, N₂ → PIS,
 * 터빈 배기 → 터빈, 복수기 안 냉각수 관 → CWS, SCS 안 C1·C2 접속 → CCWS, 축 → 발전기. SG 의 2차측 코일
 * (`S__SG_secondary`)은 공식 목록대로 증기발생기(Primary)에 둔다.
 *
 * 검출기(`__tests__/systemDiagramLayers.test.ts`)가 셋을 맞춘다: 생성본의 모든 층이 여기 있고, 카탈로그의
 * 모든 계통이 도형을 갖고, 여기 적힌 계통이 전부 카탈로그에 있다.
 *
 * **계통 이름 글자**(`DIAGRAM_NAME_LABELS`)도 같은 성격의 선언이다 — 그림의 글자는 이름(PZR·SG·TUR …)과 설명
 * («Steam supply»·«Bypass»·«C1»·«Heat sink»)이 섞여 있고 굵기 같은 꾸밈으로는 가를 수 없다(격납용기의 이름 «Containment»
 * 는 보통 굵기, 억압 풀·DHR 풀·G·C1·C2·Grid·Heat sink·SW 여덟은 굵다). 계통마다 이름 글자 도형 하나를 여기 적고, 이름만 그리는 화면
 * (마법사 `labels="system"`)은 `isSystemLabel` 로 이 선언만 읽는다. 검출기(`__tests__/systemDiagramLabels.test.tsx`)가
 * 선언된 도형이 생성본에 있고 글자를 가지며 그 계통의 도형이고 카탈로그의 짧은 이름을 담는 것을 고정한다.
 */
import type { KnownSystemId } from '@/types/scope';
import { layerOf, type DiagramShape } from './systemDiagramModel';

export type DiagramRole = KnownSystemId | 'frame';

/** 층 → 계통 */
export const DIAGRAM_LAYERS: Readonly<Record<string, DiagramRole>> = {
  UI: 'frame',
  P__Containment: 'primary/containment',
  P__Suppression_pool: 'primary/containment',
  P__DHR_pool: 'primary/pdhr',
  P__PDHR: 'primary/pdhr',
  P__PDHR__99_water: 'primary/pdhr',
  P__PDHR_steam: 'primary/pdhr',
  P__PDHR_return: 'primary/pdhr',
  // P__Reactor 는 도형 단위(아래 DIAGRAM_SHAPES) — 층 전체의 기본은 용기
  P__Reactor: 'primary/vessel',
  S__SG_secondary: 'primary/sg',
  S__SG_secondary__131_water: 'primary/sg',
  P__CVCS: 'primary/cvcs',
  P__CVCS_pipe: 'primary/cvcs',
  P__SCS: 'primary/scs',
  P__SCS_pipe: 'primary/scs',
  P__GIS: 'primary/gis',
  P__GIS_pipe: 'primary/gis',
  P__PIS: 'primary/pis',
  P__PIS_pipe: 'primary/pis',
  P__ADS: 'primary/ads',
  P__ADS_bridge_mask: 'primary/ads',
  P__CCS: 'primary/ccs',
  S__MSS: 'secondary/mss',
  S__Bypass: 'secondary/mss',
  S__Turbine: 'secondary/turbine',
  S__Exhaust: 'secondary/turbine',
  S__Condenser: 'secondary/condenser',
  S__FWS: 'secondary/feedwater',
  S__Feedwater_pump: 'secondary/feedwater',
  S__AFW: 'secondary/afw',
  S__AFW_pipe: 'secondary/afw',
  B__Shaft: 'bop/generator',
  B__Generator: 'bop/generator',
  B__Electrical: 'bop/els',
  B__Loads: 'bop/els',
  B__Grid: 'bop/els',
  B__ELS: 'bop/els',
  B__CWS: 'bop/cw',
  B__CW_pump: 'bop/cw',
  B__CW_return: 'bop/cw',
  B__Heat_sink: 'bop/cw',
  B__Condenser: 'bop/cw',
  B__CCWS: 'bop/ccw',
  B__CCWS_interface: 'bop/ccw',
  B__SW: 'bop/ccw',
};

/** 도형 이름 단위 예외 — 원자로 모듈 안의 가압기·노심·SG·RCS (나머지 P__Reactor 도형은 용기) */
export const DIAGRAM_SHAPES: Readonly<Record<string, DiagramRole>> = {
  P__Reactor__114: 'primary/pressurizer', // 가압기 상자
  P__Reactor__115: 'primary/pressurizer', // 수면
  P__Reactor__116: 'primary/pressurizer', // 라벨 PZR
  P__Reactor__120: 'primary/rcs', // 노심 배럴 벽(상승관·하강부 경계)
  P__Reactor__121: 'primary/rcs',
  P__Reactor__122: 'primary/core', // 노심 블록
  P__Reactor__123: 'primary/core', // 연료 선
  P__Reactor__124: 'primary/core',
  P__Reactor__125: 'primary/core',
  P__Reactor__126: 'primary/core',
  P__Reactor__127: 'primary/core',
  P__Reactor__128: 'primary/core', // 라벨 CORE
  P__Reactor__129: 'primary/core', // 라벨 지시선
  P__Reactor__130: 'primary/sg', // SG 외피
  P__Reactor__132: 'primary/sg', // 라벨 SG
  P__Reactor__133: 'primary/rcs', // 용기 안 유로
  P__Reactor__134: 'primary/rcs', // 유동 화살표
  P__Reactor__135: 'primary/rcs',
  P__Reactor__136: 'primary/rcs',
  P__Reactor__137: 'primary/rcs',
  P__Reactor__138: 'primary/rcs',
  P__Reactor__139: 'primary/rcs', // 라벨 RCS
};

/** 계통 → 그 이름을 적은 글자 도형(하나씩). 카탈로그의 계통이 늘면 타입이 여기 한 줄을 요구한다 */
export const DIAGRAM_NAME_LABELS: Readonly<Record<KnownSystemId, string>> = {
  'primary/core': 'P__Reactor__128', // CORE
  'primary/vessel': 'P__Reactor__140', // RPV
  'primary/rcs': 'P__Reactor__139', // RCS
  'primary/pressurizer': 'P__Reactor__116', // PZR
  'primary/sg': 'P__Reactor__132', // SG
  'primary/containment': 'P__Containment__94', // Containment(보통 굵기)
  'primary/pdhr': 'P__PDHR__100', // PDHR · Standby / emergency
  'primary/cvcs': 'P__CVCS__240', // CVCS
  'primary/scs': 'P__SCS__258', // SCS
  'primary/gis': 'P__GIS__223', // GIS
  'primary/pis': 'P__PIS__234', // PIS
  'primary/ads': 'P__ADS__282', // ADS
  'primary/ccs': 'P__CCS__295', // CCS
  'secondary/mss': 'S__MSS__144', // MSS / Steam
  'secondary/turbine': 'S__Turbine__155', // TUR
  'secondary/condenser': 'S__Condenser__162', // CNR
  'secondary/feedwater': 'S__Feedwater_pump__168', // FWS
  'secondary/afw': 'S__AFW__314', // AFW
  'bop/generator': 'B__Generator__180', // GEN
  'bop/cw': 'B__CW_pump__207', // CWS
  'bop/els': 'B__ELS__182', // ELS
  'bop/ccw': 'B__CCWS__325', // CCWS
};

/** 도형 → 계통(또는 틀). 선언에 없는 층은 오류 — 원본에 새 층이 생기면 여기 한 줄을 적어야 한다 */
export function diagramRoleOf(shapeName: string): DiagramRole {
  const byShape = DIAGRAM_SHAPES[shapeName];
  if (byShape) return byShape;
  const role = DIAGRAM_LAYERS[layerOf(shapeName)];
  if (!role) throw new Error(`계통도 층이 선언에 없다: ${shapeName}`);
  return role;
}

const NAME_LABEL_SHAPES: ReadonlySet<string> = new Set(Object.values(DIAGRAM_NAME_LABELS));

/** 계통 **이름** 글자인가 — 선언(`DIAGRAM_NAME_LABELS`)에 적힌 도형만. 이름만 그리는 화면(마법사 `labels="system"`)이 이 술어로 거른다 */
export const isSystemLabel = (shape: DiagramShape): boolean => NAME_LABEL_SHAPES.has(shape.name);

